import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "/" },
  {
    label: "Insurance",
    href: "#",
    dropdown: [
      { label: "Health Insurance", href: "/health-insurance" },
      { label: "Health Insurers & Brochures", href: "/health-insurance-companies" },
      { label: "Term Insurance", href: "/term-insurance" },
      { label: "Life Insurance", href: "/life-insurance" },
      { label: "Car Insurance", href: "/car-insurance" },
      { label: "Travel Insurance", href: "/travel-insurance" },
      { label: "Marine Insurance", href: "/marine-insurance" },
      { label: "Corporate Insurance", href: "/corporate-insurance" },
      { label: "Group Health Insurance", href: "/group-health-insurance" },
    ],
  },
  {
    label: "Investments",
    href: "#",
    dropdown: [
      { label: "Mutual Funds", href: "/mutual-funds" },
      { label: "SIP Investments", href: "/sip-investments" },
      { label: "Retirement Planning", href: "/retirement-planning" },
      { label: "Financial Planning", href: "/financial-planning" },
    ],
  },
  { label: "Compare Plans", href: "/compare-plans" },
  { label: "Calculators", href: "/calculators" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const toggleDropdown = useCallback((label: string) => {
    setActiveDropdown((prev) => (prev === label ? null : label));
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                <i className="ri-shield-check-line text-white text-lg"></i>
              </div>
              <div className="flex flex-col">
                <span className={`text-lg md:text-xl font-bold leading-tight ${scrolled ? "text-slate-900" : "text-white"}`}>
                  ExpertAdvise
                </span>
                <span className={`text-[10px] leading-tight tracking-wider uppercase ${scrolled ? "text-slate-500" : "text-white/80"}`}>
                  Insurance & Finance
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {link.dropdown ? (
                    <>
                      <button
                        className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          scrolled
                            ? "text-slate-700 hover:text-primary-600 hover:bg-primary-50"
                            : "text-white/90 hover:text-white hover:bg-white/10"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleDropdown(link.label);
                        }}
                      >
                        {link.label}
                        <i className={`ri-arrow-down-s-line transition-transform ${activeDropdown === link.label ? "rotate-180" : ""}`}></i>
                      </button>
                      {activeDropdown === link.label && (
                        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-2 overflow-hidden animate-fade-in-up">
                          {link.dropdown.map((sub) => (
                            <Link
                              key={sub.label}
                              to={sub.href}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                            >
                              <i className="ri-arrow-right-s-line text-primary-500"></i>
                              {sub.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.href}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        scrolled
                          ? "text-slate-700 hover:text-primary-600 hover:bg-primary-50"
                          : "text-white/90 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop CTA + Contact */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+919999365655"
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  scrolled ? "text-slate-600 hover:text-primary-600" : "text-white/80 hover:text-white"
                }`}
              >
                <i className="ri-phone-line"></i>
                +91 99993 65655
              </a>
              <Link
                to="/book-appointment?source=Homepage"
                className="bg-cta-600 hover:bg-cta-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all shadow-md hover:shadow-lg whitespace-nowrap"
              >
                Book Free Consultation
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              className={`lg:hidden p-2.5 rounded-lg cursor-pointer transition-colors ${
                scrolled ? "text-slate-700 hover:bg-slate-100" : "text-white hover:bg-white/10"
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-white transition-transform duration-300 ease-in-out lg:hidden ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!mobileOpen}
      >
        {/* Mobile Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
              <i className="ri-shield-check-line text-white text-lg"></i>
            </div>
            <span className="text-lg font-bold text-slate-900">ExpertAdvise</span>
          </Link>
          <button
            className="p-2.5 rounded-lg text-slate-700 hover:bg-slate-100 cursor-pointer"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Mobile Scrollable Content */}
        <div className="h-[calc(100dvh-64px)] overflow-y-auto px-4 py-4">
          <div className="space-y-1">
            {navLinks.map((link) => (
              <div key={link.label}>
                {link.dropdown ? (
                  <div className="rounded-xl overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-4 py-4 text-base font-semibold text-slate-800 hover:bg-slate-50 rounded-xl transition-colors cursor-pointer"
                      onClick={() => toggleDropdown(link.label)}
                      aria-expanded={activeDropdown === link.label}
                    >
                      <span>{link.label}</span>
                      <i
                        className={`ri-arrow-down-s-line text-xl text-slate-400 transition-transform duration-200 ${
                          activeDropdown === link.label ? "rotate-180" : ""
                        }`}
                      ></i>
                    </button>
                    <div
                      className="grid transition-all duration-200 ease-in-out"
                      style={{
                        gridTemplateRows: activeDropdown === link.label ? "1fr" : "0fr",
                      }}
                    >
                      <div className="overflow-hidden">
                        <div className="ml-3 pl-4 border-l-2 border-primary-100 space-y-1 py-1">
                          {link.dropdown.map((sub) => (
                            <Link
                              key={sub.label}
                              to={sub.href}
                              className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-slate-600 hover:text-primary-700 hover:bg-primary-50/50 rounded-lg transition-colors"
                              onClick={() => setMobileOpen(false)}
                            >
                              <i className="ri-arrow-right-s-line text-primary-500"></i>
                              {sub.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={link.href}
                    className="block px-4 py-4 text-base font-semibold text-slate-800 hover:bg-slate-50 rounded-xl transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile CTA Section */}
          <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
            <a
              href="tel:+919999365655"
              className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                <i className="ri-phone-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <div className="text-xs text-slate-400">Call us anytime</div>
                <div className="text-sm font-semibold text-slate-900">+91 99993 65655</div>
              </div>
            </a>
            <Link
              to="/book-appointment?source=Homepage"
              className="block w-full text-center bg-cta-600 hover:bg-cta-700 text-white text-base font-semibold px-5 py-4 rounded-xl transition-colors shadow-sm"
              onClick={() => setMobileOpen(false)}
            >
              Book Free Consultation
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}